import { Modal, message } from 'antd'

const DEFAULT_USER_MESSAGE = 'Something went wrong while processing your request. Please try again.'
const baseModalError = Modal.error.bind(Modal) as (config: Record<string, unknown>) => unknown

/** After this long, session-expired modal is closed and user is sent to login (same as dismissing the modal). */
const SESSION_EXPIRED_MODAL_FALLBACK_MS = 10_000

let sessionExpiryRedirectTimer: ReturnType<typeof setTimeout> | undefined
let sessionExpiryLogoutDone = false

const clearSessionExpiryRedirectTimer = (): void => {
  if (sessionExpiryRedirectTimer !== undefined) {
    clearTimeout(sessionExpiryRedirectTimer)
    sessionExpiryRedirectTimer = undefined
  }
}

const performSessionExpiredLogout = (): void => {
  if (sessionExpiryLogoutDone) {
    return
  }
  sessionExpiryLogoutDone = true
  clearSessionExpiryRedirectTimer()
  try {
    localStorage.removeItem('userInfo')
  } catch {
    // ignore storage errors (private mode, etc.)
  }
  window.location.href = '/'
}

const toLowerText = (value: unknown): string => String(value ?? '').toLowerCase().trim()
const getCurrentPath = (): string =>
  typeof window !== 'undefined' && typeof window.location?.pathname === 'string'
    ? window.location.pathname
    : ''

const isSessionExpiredContent = (content: string): boolean => {
  if (content === 'Your session has expired. Please sign in again to continue.') {
    return true
  }
  const n = toLowerText(content)
  return (
    n.includes('session has expired') ||
    n.includes('please sign in again') ||
    n.includes('please login again') ||
    n.includes('authentication failed') ||
    n.includes('invalid token')
  )
}

const getObjectMessage = (value: Record<string, unknown>): string | undefined => {
  const candidateKeys = ['message', 'error', 'detail', 'title', 'description']
  for (const key of candidateKeys) {
    const candidate = value[key]
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate.trim()
    }
  }
  return undefined
}

const extractErrorText = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim()
  }
  if (value instanceof Error) {
    return value.message.trim()
  }
  if (typeof value === 'object' && value !== null) {
    const objectValue = value as Record<string, unknown>

    if ('response' in objectValue && typeof objectValue.response === 'object' && objectValue.response !== null) {
      const responseObject = objectValue.response as Record<string, unknown>
      if ('data' in responseObject) {
        const responseData = responseObject.data
        if (typeof responseData === 'string') {
          return responseData.trim()
        }
        if (typeof responseData === 'object' && responseData !== null) {
          const nestedMessage = getObjectMessage(responseData as Record<string, unknown>)
          if (nestedMessage !== undefined) {
            return nestedMessage
          }
        }
      }
    }

    const objectMessage = getObjectMessage(objectValue)
    if (objectMessage !== undefined) {
      return objectMessage
    }
  }

  return ''
}

const extractBackendMessage = (value: unknown): string | undefined => {
  if (typeof value !== 'object' || value === null) {
    return undefined
  }

  const objectValue = value as Record<string, unknown>
  if (!('response' in objectValue) || typeof objectValue.response !== 'object' || objectValue.response === null) {
    return undefined
  }

  const responseObject = objectValue.response as Record<string, unknown>
  if (!('data' in responseObject)) {
    return undefined
  }

  const responseData = responseObject.data
  if (typeof responseData === 'string' && responseData.trim() !== '') {
    return responseData.trim()
  }

  if (typeof responseData === 'object' && responseData !== null) {
    return getObjectMessage(responseData as Record<string, unknown>)
  }

  return undefined
}

const getFriendlyMessageByPattern = (rawMessage: string): string | null => {
  const normalized = toLowerText(rawMessage)

  if (normalized.includes('network error') || normalized.includes('failed to fetch')) {
    return 'Unable to connect to the server. Please check your internet connection and try again.'
  }
  if (normalized.includes('timeout')) {
    return 'The server is taking too long to respond. Please try again in a moment.'
  }
  if (
    normalized.includes('unauthorized') ||
    normalized.includes('invalid token') ||
    normalized.includes('jwt') ||
    normalized.includes('session expired')
  ) {
    const currentPath = getCurrentPath()
    if (
      currentPath.includes('/refund-request/bidSecurityReference/') ||
      currentPath.includes('/refund-request/requestBidSecurityFees/')
    ) {
      return 'Unable to load this refund request link. Please verify the reference number and try again.'
    }
    return 'Your session has expired. Please sign in again to continue.'
  }
  if (normalized.includes('forbidden') || normalized.includes('access denied')) {
    return 'You do not have permission to perform this action.'
  }
  if (
    normalized.includes('not allowed to perform this operation') ||
    normalized.includes('you are not allowed')
  ) {
    return 'You do not have permission to perform this action.'
  }
  return null
}

const sanitizeTechnicalText = (rawMessage: string): string => {
  if (rawMessage.trim() === '') {
    return DEFAULT_USER_MESSAGE
  }

  const withoutStatus = rawMessage
    .replace(/request failed with status code \d+/gi, '')
    .replace(/axioserror[:\s-]*/gi, '')
    .replace(/^error[:\s-]*/gi, '')
    .trim()

  if (withoutStatus === '') {
    return DEFAULT_USER_MESSAGE
  }

  if (
    /^((err|e)[-_]?\d+|[a-z]+exception)$/i.test(withoutStatus) ||
    withoutStatus.includes('{') ||
    withoutStatus.includes('stack trace')
  ) {
    return DEFAULT_USER_MESSAGE
  }

  return withoutStatus
}

/**
 * True when the failure is almost certainly "no server" / network (no HTTP body to show).
 * Used to avoid modal spam and to let lists render empty instead.
 */
export const isConnectivityFailure = (value: unknown): boolean => {
  if (typeof value === 'string') {
    const s = value.toLowerCase()
    return (
      s.includes('unable to connect') ||
      s.includes('network error') ||
      s.includes('failed to fetch') ||
      s.includes('load failed') ||
      s.includes('err_network') ||
      s.includes('connection refused') ||
      s.includes('econnrefused') ||
      (s.includes('timeout') && (s.includes('server') || s.includes('network')))
    )
  }
  if (value instanceof Error) {
    return isConnectivityFailure(value.message)
  }
  if (typeof value === 'object' && value !== null) {
    const o = value as Record<string, unknown>
    const response = o.response
    if (response !== undefined && response !== null) {
      return false
    }
    const code = String(o.code ?? '')
    const msg = String(o.message ?? '')
    if (code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ECONNREFUSED') {
      return true
    }
    if (msg === 'Network Error') {
      return true
    }
    return isConnectivityFailure(msg)
  }
  return false
}

export const normalizeErrorMessage = (value: unknown): string => {
  if (typeof value === 'object' && value !== null && 'response' in (value as Record<string, unknown>)) {
    const status = (value as { response?: { status?: number } }).response?.status
    if (status === 401) {
      const currentPath = getCurrentPath()
      if (
        currentPath.includes('/refund-request/bidSecurityReference/') ||
        currentPath.includes('/refund-request/requestBidSecurityFees/')
      ) {
        return 'Unable to load this refund request link. Please verify the reference number and try again.'
      }
      return 'Your session has expired. Please sign in again to continue.'
    }
  }

  const backendMessage = extractBackendMessage(value)
  if (backendMessage !== undefined && backendMessage.trim() !== '') {
    return sanitizeTechnicalText(backendMessage)
  }

  const extractedMessage = extractErrorText(value)
  const friendlyMessage = getFriendlyMessageByPattern(extractedMessage)

  if (friendlyMessage !== null) {
    return friendlyMessage
  }

  return sanitizeTechnicalText(extractedMessage)
}

interface FriendlyPopupOptions {
  title?: string
}

const getFriendlyPopupMeta = (content: string): { title: string, okText: string } => {
  const normalized = toLowerText(content)

  if (isSessionExpiredContent(content)) {
    return { title: 'Session Expired', okText: 'Sign in again' }
  }

  if (content === 'You do not have permission to perform this action.') {
    return { title: 'Access Restricted', okText: 'Close' }
  }

  if (normalized.includes('not found') || normalized.includes('does not exist')) {
    return { title: 'Information Not Found', okText: 'Close' }
  }

  if (normalized.includes('invalid') || normalized.includes('required') || normalized.includes('validation')) {
    return { title: 'Please Check Your Details', okText: 'Review' }
  }

  if (normalized.includes('unable to connect') || normalized.includes('timeout')) {
    return { title: 'Connection Issue', okText: 'Try again' }
  }

  return { title: 'Unable to Complete Request', okText: 'Close' }
}

const getContentFromMessageArgs = (args: unknown[]): unknown => {
  for (const arg of args) {
    if (typeof arg === 'object' && arg !== null && 'response' in (arg as Record<string, unknown>)) {
      return arg
    }
  }

  const firstArg = args[0]
  if (typeof firstArg === 'object' && firstArg !== null && 'content' in (firstArg as Record<string, unknown>)) {
    const contentValue = (firstArg as Record<string, unknown>).content
    if (
      typeof contentValue === 'object' &&
      contentValue !== null &&
      'response' in (contentValue as Record<string, unknown>)
    ) {
      return contentValue
    }
    return contentValue
  }
  return firstArg
}

export const showFriendlyErrorPopup = (value: unknown, options?: FriendlyPopupOptions): unknown => {
  if (isConnectivityFailure(value)) {
    return Promise.resolve()
  }
  const content = normalizeErrorMessage(value)
  if (isConnectivityFailure(content)) {
    return Promise.resolve()
  }
  const { title, okText } = getFriendlyPopupMeta(content)
  const isSessionExpired = isSessionExpiredContent(content)

  clearSessionExpiryRedirectTimer()

  const modalConfig: Record<string, unknown> = {
    title: options?.title ?? title,
    content,
    centered: true,
    width: 440,
    okText,
    maskClosable: true,
    className: 'friendly-error-modal modal-enhanced',
    ...(isSessionExpired
      ? {
          afterClose: () => {
            performSessionExpiredLogout()
          }
        }
      : {})
  }

  const modalApi = baseModalError(modalConfig) as { destroy?: () => void }

  if (isSessionExpired) {
    sessionExpiryRedirectTimer = setTimeout(() => {
      sessionExpiryRedirectTimer = undefined
      if (typeof modalApi?.destroy === 'function') {
        modalApi.destroy()
      } else {
        performSessionExpiredLogout()
      }
    }, SESSION_EXPIRED_MODAL_FALLBACK_MS)
  }

  return modalApi
}

let isErrorMessageOverridden = false

export const installGlobalErrorPopupHandling = (): void => {
  if (isErrorMessageOverridden) {
    return
  }

  isErrorMessageOverridden = true
  ;(message as unknown as { error: (...args: unknown[]) => unknown }).error = (...args: unknown[]): unknown => {
    const messageContent = getContentFromMessageArgs(args)
    if (isConnectivityFailure(messageContent)) {
      return Promise.resolve()
    }
    return showFriendlyErrorPopup(messageContent)
  }

  ;(Modal as unknown as { error: (config: Record<string, unknown>) => unknown }).error = (
    config: Record<string, unknown>
  ): unknown => {
    const title = typeof config?.title === 'string' ? config.title : undefined
    const content = config?.content
    if (isConnectivityFailure(content)) {
      return Promise.resolve()
    }
    if (typeof content === 'string' || content instanceof Error) {
      return showFriendlyErrorPopup(content, { title })
    }

    if (
      typeof content === 'object' &&
      content !== null &&
      'response' in (content as Record<string, unknown>)
    ) {
      return showFriendlyErrorPopup(content, { title })
    }

    return baseModalError(config)
  }
}
